import type { FaqRow } from '@antreja/ui/faq';
import { AppError, errorInfoSync, toAppError } from '@antreja/ui/errors';
import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  AccessCodeInput,
  AccessRuleInput,
  BuildingInput,
  ClubAdminInviteInput,
  ClubAuditFilter,
  ClubAuditPage,
  ClubAdminInviteResult,
  CreatedCode,
  DoorInput,
  EventFilter,
  GroupInput,
  ImportInput,
  ImportResult,
  InstallerRow,
  InvitePersonInput,
  InvitePersonResult,
  LocationInput,
  OrganizationInput,
  PersonInput,
  Repository,
  Session,
  StaffInviteInput,
  StaffInviteResult,
  ClubInstallerRow,
} from '../repository';
import type { LockConfigState,
  AccessCode,
  AccessEvent,
  AccessRule,
  AuditEntry,
  Building,
  ClubAdminRow,
  ClubAuditRow,
  Credential,
  Door,
  Group,
  GroupManager,
  GroupMember,
  InstallerAssignment,
  Location,
  Lock,
  LockGrant,
  LockHardening,
  LockModel,
  LockPlacement,
  Membership,
  OrgRole,
  Organization,
  Person,
  PersonAppStatusRow,
  PlatformRole,
  PlatformStaff,
  Profile,
  StaffRow,
  SyncJob,
  CardLoan,
  LoanCardRow,
} from '../../lib/types';
import type { InventoryQuery, InventoryResult } from '../../lib/inventory';

type Result<T> = { data: T | null; error: { message: string } | null };

/** PostgREST-fout → AppError (code/status/melding blijven bewaard voor de foutvertaling en support). */
function unwrap<T>(res: Result<T>): T {
  if (res.error) throw new AppError(errorInfoSync(res.error, 'database'));
  return res.data as T;
}

/**
 * Supabase implementation. All reads/writes go through RLS (publishable key + user JWT).
 * Derived state (lock_grants, sync_jobs) is produced by DB triggers and the access compiler
 * Edge Function; the portal never writes it directly.
 *
 * Server endpoints (all with server-side authz + audit_log): Edge Functions `create-access-code`,
 * `invite-club-admin`, `invite-person`; RPCs `set_passage_mode(p_door_id, p_enabled)`, `start_impersonation(p_organization_id,
 * p_reason)` (supabase/migrations/…_security_fixes.sql), `person_app_status`, `revoke_person_invite`,
 * `unlink_person_app` (…_person_invites.sql); onboarding (…_onboarding.sql): `list_platform_staff`,
 * `set_platform_staff_role`, `remove_platform_staff`, `save_location`, `delete_location`, `save_door`, `archive_door`,
 * `link_lock_to_door`, `unlink_lock_from_door`, `list_club_admins`, `set_member_role`, `remove_club_role`; Edge Function
 * `invite-staff`. Access codes are revoked, never deleted.
 */
export class SupabaseRepository implements Repository {
  readonly mode = 'supabase' as const;
  constructor(private sb: SupabaseClient) {}

  async getSessionContext(profileId: string): Promise<Session> {
    const [profile, staff, memberships, managed, assignments] = await Promise.all([
      this.sb.from('profiles').select('id, full_name, phone, locale, ui_theme').eq('id', profileId).maybeSingle(),
      this.sb.from('platform_staff').select('profile_id, role').eq('profile_id', profileId).maybeSingle(),
      this.sb.from('memberships').select('*').eq('profile_id', profileId),
      this.sb.from('group_managers').select('group_id').eq('profile_id', profileId),
      this.sb
        .from('installer_assignments')
        .select('organization_id, kind')
        .eq('profile_id', profileId)
        .gt('valid_until', new Date().toISOString()),
    ]);
    const p = unwrap(profile as Result<Profile | null>);
    if (!p) throw new Error('Je account heeft nog geen profiel. Vraag je clubbeheerder om een uitnodiging.');
    const platformRole = (unwrap(staff as Result<PlatformStaff | null>))?.role ?? null;
    // Zelfde regel als app.profile_is_installer_for: clubtoewijzing altijd, Antreja-toewijzing alleen voor installateur/beheerder.
    const validAssignments = (unwrap(assignments as Result<{ organization_id: string; kind: string | null }[]>) ?? []).filter(
      (a) => a.kind === 'club' || platformRole === 'installer' || platformRole === 'platform_admin',
    );
    return {
      profile: p,
      platformRole,
      memberships: unwrap(memberships as Result<Membership[]>) ?? [],
      managedGroupIds: (unwrap(managed as Result<{ group_id: string }[]>) ?? []).map((g) => g.group_id),
      installerOrgIds: validAssignments.map((a) => a.organization_id),
    };
  }

  async listOrganizations() {
    return unwrap(await this.sb.from('organizations').select('*').is('archived_at', null).order('name')) as Organization[];
  }
  async getOrganization(orgId: string) {
    return unwrap(await this.sb.from('organizations').select('*').eq('id', orgId).maybeSingle()) as Organization | null;
  }
  async updateOrganization(orgId: string, patch: Partial<Organization>) {
    return unwrap(await this.sb.from('organizations').update(patch).eq('id', orgId).select().single()) as Organization;
  }
  async createOrganization(input: OrganizationInput) {
    const { data, error } = await this.sb.functions.invoke('invite-club-admin', { body: input });
    if (error) throw await toAppError(error, 'invite-club-admin');
    return (data as { organization: Organization }).organization;
  }
  async listMemberships(orgId: string) {
    return unwrap(
      await this.sb.from('memberships').select('*, profile:profiles(id, full_name, phone, locale)').eq('organization_id', orgId),
    ) as (Membership & { profile: Profile })[];
  }

  async listClubAdmins(orgId: string) {
    return (unwrap(await this.sb.rpc('list_club_admins', { p_organization_id: orgId })) ?? []) as ClubAdminRow[];
  }
  async inviteClubAdmin(input: ClubAdminInviteInput): Promise<ClubAdminInviteResult> {
    const { data, error } = await this.sb.functions.invoke('invite-club-admin', { body: input });
    if (error) throw await toAppError(error, 'invite-club-admin');
    return data as ClubAdminInviteResult;
  }
  async resendClubAdminInvite(orgId: string, profileId: string) {
    const { data, error } = await this.sb.functions.invoke('invite-club-admin', { body: { organization_id: orgId, resend_profile_id: profileId } });
    if (error) throw await toAppError(error, 'invite-club-admin');
    return data as { email: string };
  }
  async setMemberRole(orgId: string, profileId: string, role: OrgRole) {
    unwrap(await this.sb.rpc('set_member_role', { p_organization_id: orgId, p_profile_id: profileId, p_role: role }));
  }
  async removeClubRole(orgId: string, profileId: string) {
    unwrap(await this.sb.rpc('remove_club_role', { p_organization_id: orgId, p_profile_id: profileId }));
  }

  async listLocations(orgId: string) {
    return unwrap(await this.sb.from('locations').select('*').eq('organization_id', orgId).order('name')) as Location[];
  }
  async listDoors(orgId: string) {
    return unwrap(
      await this.sb.from('doors').select('*').eq('organization_id', orgId).is('archived_at', null).order('name'),
    ) as Door[];
  }
  async listLocks(orgId: string | null) {
    let q = this.sb.from('locks').select('*').order('serial');
    if (orgId) q = q.eq('organization_id', orgId);
    return unwrap(await q) as Lock[];
  }
  async searchInventory(q: InventoryQuery): Promise<InventoryResult> {
    const data = unwrap(
      await this.sb.rpc('inventory_search', {
        p_search: q.search?.trim() || null,
        p_status: q.status || null,
        p_model: q.model || null,
        p_org: q.org || null,
        p_sort: q.sort,
        p_dir: q.dir,
        p_limit: q.limit,
        p_offset: q.offset,
      }),
    ) as { lock: Lock; organization_name: string | null; door_id: string | null; door_name: string | null; location_name: string | null; total_count: number | string }[] | null;
    const list = data ?? [];
    return {
      rows: list.map(({ total_count: _t, ...r }) => r),
      total: list.length ? Number(list[0]!.total_count) : 0,
    };
  }
  async addLocks(input: { model: LockModel; serials: string[]; firmware: string | null }) {
    const rows = input.serials.map((serial) => ({ model: input.model, serial, firmware: input.firmware }));
    return unwrap(await this.sb.from('locks').insert(rows).select()) as Lock[];
  }
  async assignLock(lockId: string, orgId: string | null) {
    return unwrap(
      await this.sb
        .from('locks')
        .update({ organization_id: orgId, status: orgId ? 'assigned' : 'in_stock' })
        .eq('id', lockId)
        .select()
        .single(),
    ) as Lock;
  }
  async emergencyResetLock(lockId: string) {
    const { error } = await this.sb.functions.invoke('lock-home', { body: { action: 'cloud_remove', lock_id: lockId } });
    if (error) throw await toAppError(error, 'lock-home');
  }
  async setLockModel(lockId: string, model: LockModel) {
    return unwrap(await this.sb.rpc('set_lock_model', { p_lock_id: lockId, p_model: model })) as Lock;
  }
  async markBatteryReplaced(lockId: string) {
    unwrap(await this.sb.rpc('mark_battery_replaced', { p_lock_id: lockId, p_battery_pct: 100 }));
  }
  async setDoorAutoLock(doorId: string, enabled: boolean, seconds: number) {
    unwrap(await this.sb.rpc('set_door_auto_lock', { p_door_id: doorId, p_enabled: enabled, p_seconds: seconds }));
  }
  async getDoorLockConfigState(doorId: string) {
    return (unwrap(await this.sb.rpc('door_lock_config_state', { p_door_id: doorId })) ?? 'unknown') as LockConfigState;
  }
  async setPassageMode(doorId: string, enabled: boolean) {
    unwrap(await this.sb.rpc('set_passage_mode', { p_door_id: doorId, p_enabled: enabled }));
  }

  async saveLocation(orgId: string, input: LocationInput) {
    return unwrap(
      await this.sb.rpc('save_location', {
        p_organization_id: orgId,
        p_location_id: input.id,
        p_name: input.name,
        p_street: input.street,
        p_house_number: input.house_number,
        p_house_number_addition: input.house_number_addition,
        p_postal_code: input.postal_code,
        p_city: input.city,
        p_country: input.country || 'NL',
      }),
    ) as string;
  }
  async deleteLocation(locationId: string) {
    unwrap(await this.sb.rpc('delete_location', { p_location_id: locationId }));
  }
  async listBuildings(orgId: string) {
    return unwrap(await this.sb.from('buildings').select('*').eq('organization_id', orgId).order('name')) as Building[];
  }
  async saveBuilding(orgId: string, input: BuildingInput) {
    return unwrap(
      await this.sb.rpc('save_building', {
        p_organization_id: orgId,
        p_building_id: input.id,
        p_location_id: input.location_id,
        p_name: input.name,
        p_notes: input.notes,
      }),
    ) as string;
  }
  async archiveBuilding(buildingId: string) {
    unwrap(await this.sb.rpc('archive_building', { p_building_id: buildingId }));
  }
  async saveDoor(orgId: string, input: DoorInput) {
    return unwrap(
      await this.sb.rpc('save_door', {
        p_organization_id: orgId,
        p_door_id: input.id,
        p_building_id: input.building_id,
        p_name: input.name,
        p_kind: input.kind,
        p_escape_route: input.escape_route,
        p_passage_mode_allowed: input.passage_mode_allowed,
      }),
    ) as string;
  }
  async archiveDoor(doorId: string) {
    unwrap(await this.sb.rpc('archive_door', { p_door_id: doorId }));
  }
  async linkLockToDoor(doorId: string, lockId: string) {
    unwrap(await this.sb.rpc('link_lock_to_door', { p_door_id: doorId, p_lock_id: lockId }));
  }
  async unlinkLockFromDoor(doorId: string) {
    unwrap(await this.sb.rpc('unlink_lock_from_door', { p_door_id: doorId }));
  }
  async listLockPlacements(): Promise<LockPlacement[]> {
    const data = unwrap(
      await this.sb
        .from('doors')
        .select('id, name, organization_id, lock_id, location:locations(name)')
        .not('lock_id', 'is', null)
        .is('archived_at', null),
    ) as unknown as { id: string; name: string; organization_id: string; lock_id: string; location: { name: string } | null }[];
    return data.map((d) => ({
      lock_id: d.lock_id,
      door_id: d.id,
      door_name: d.name,
      location_name: d.location?.name ?? '',
      organization_id: d.organization_id,
    }));
  }

  async listPersons(orgId: string) {
    return unwrap(
      await this.sb.from('persons').select('*').eq('organization_id', orgId).is('archived_at', null).order('full_name'),
    ) as Person[];
  }
  async createPerson(orgId: string, input: PersonInput) {
    return unwrap(await this.sb.from('persons').insert({ ...input, organization_id: orgId }).select().single()) as Person;
  }
  async createPersonInGroup(groupId: string, input: PersonInput, validUntil: string | null) {
    const id = unwrap(
      await this.sb.rpc('create_person_in_group', {
        p_group_id: groupId,
        p_full_name: input.full_name,
        p_email: input.email,
        p_phone: input.phone,
        p_external_ref: input.external_ref,
        p_is_minor: input.is_minor,
        p_valid_until: validUntil,
      }),
    ) as string;
    return unwrap(await this.sb.from('persons').select('*').eq('id', id).single()) as Person;
  }
  async archivePerson(personId: string) {
    unwrap(await this.sb.from('persons').update({ archived_at: new Date().toISOString() }).eq('id', personId));
  }
  async setMyTheme(profileId: string, theme: 'system' | 'light' | 'dark') {
    unwrap(await this.sb.from('profiles').update({ ui_theme: theme }).eq('id', profileId));
  }
  async listFaqItems() {
    return (unwrap(
      await this.sb.from('faq_items').select('id, persona, surfaces, question, answer, sort_order, published, updated_at').order('sort_order'),
    ) ?? []) as FaqRow[];
  }
  async saveFaqItem(row: FaqRow) {
    const { updated_at: _u, ...rest } = row;
    unwrap(await this.sb.from('faq_items').upsert(rest));
  }
  async deleteFaqItem(id: string) {
    unwrap(await this.sb.from('faq_items').delete().eq('id', id));
  }
  async listLoanCards(orgId: string) {
    return (unwrap(await this.sb.rpc('loan_cards', { p_organization_id: orgId })) ?? []) as LoanCardRow[];
  }
  async createLoanCard(groupId: string, label: string) {
    return unwrap(await this.sb.rpc('create_loan_card', { p_group_id: groupId, p_label: label })) as string;
  }
  async lendLoanCard(personId: string, guestName: string, guestClub: string | null, dueAt: string) {
    unwrap(await this.sb.rpc('lend_loan_card', { p_person_id: personId, p_guest_name: guestName, p_guest_club: guestClub, p_due_at: dueAt }));
  }
  async returnLoanCard(personId: string) {
    unwrap(await this.sb.rpc('return_loan_card', { p_person_id: personId }));
  }
  async loanCardHistory(personId: string) {
    return (unwrap(await this.sb.rpc('loan_card_history', { p_person_id: personId })) ?? []) as CardLoan[];
  }
  async listClubInstallers(orgId: string) {
    return (unwrap(await this.sb.rpc('club_installers', { p_organization_id: orgId })) ?? []) as ClubInstallerRow[];
  }
  async assignClubInstaller(personId: string, validUntil: string) {
    unwrap(await this.sb.rpc('assign_club_installer', { p_person_id: personId, p_valid_until: validUntil }));
  }
  async endClubInstaller(personId: string) {
    unwrap(await this.sb.rpc('end_club_installer', { p_person_id: personId }));
  }
  async listPersonAppStatus(orgId: string) {
    return (unwrap(await this.sb.rpc('person_app_status', { p_organization_id: orgId })) ?? []) as PersonAppStatusRow[];
  }
  async invitePerson(input: InvitePersonInput): Promise<InvitePersonResult> {
    const { data, error } = await this.sb.functions.invoke('invite-person', { body: input });
    if (error) throw await toAppError(error, 'invite-person');
    return data as InvitePersonResult;
  }
  async revokePersonInvite(inviteId: string) {
    unwrap(await this.sb.rpc('revoke_person_invite', { p_invite_id: inviteId }));
  }
  async unlinkPersonApp(personId: string) {
    unwrap(await this.sb.rpc('unlink_person_app', { p_person_id: personId }));
  }
  async listGroups(orgId: string) {
    return unwrap(
      await this.sb.from('groups').select('*').eq('organization_id', orgId).is('archived_at', null).order('name'),
    ) as Group[];
  }
  async createGroup(orgId: string, input: GroupInput) {
    return unwrap(await this.sb.from('groups').insert({ ...input, organization_id: orgId }).select().single()) as Group;
  }
  async duplicateGroup(groupId: string, input: GroupInput & { copy_member_ids: string[]; member_valid_until: string | null }) {
    const src = unwrap(await this.sb.from('groups').select('*').eq('id', groupId).single()) as Group;
    const g = await this.createGroup(src.organization_id, input);
    const rules = unwrap(await this.sb.from('access_rules').select('*').eq('group_id', groupId)) as AccessRule[];
    if (rules.length) {
      unwrap(
        await this.sb.from('access_rules').insert(
          rules.map(({ id: _id, ...r }) => ({ ...r, group_id: g.id })),
        ),
      );
    }
    if (input.copy_member_ids.length) {
      unwrap(
        await this.sb.from('group_members').insert(
          input.copy_member_ids.map((person_id) => ({ group_id: g.id, person_id, valid_until: input.member_valid_until })),
        ),
      );
    }
    return g;
  }
  async listGroupMembers(orgId: string) {
    return unwrap(
      await this.sb.from('group_members').select('*, groups!inner(organization_id)').eq('groups.organization_id', orgId),
    ) as GroupMember[];
  }
  async addGroupMember(input: { group_id: string; person_id: string; valid_until: string | null }) {
    return unwrap(await this.sb.from('group_members').insert(input).select().single()) as GroupMember;
  }
  async removeGroupMember(groupId: string, personId: string) {
    unwrap(await this.sb.from('group_members').delete().eq('group_id', groupId).eq('person_id', personId));
  }
  async listGroupManagers(orgId: string) {
    return unwrap(
      await this.sb
        .from('group_managers')
        .select('*, profile:profiles(id, full_name, phone, locale), groups!inner(organization_id)')
        .eq('groups.organization_id', orgId),
    ) as (GroupManager & { profile: Profile })[];
  }
  async listAccessRules(orgId: string) {
    return unwrap(await this.sb.from('access_rules').select('*').eq('organization_id', orgId)) as AccessRule[];
  }
  async saveAccessRules(groupId: string, rules: AccessRuleInput[]) {
    const group = unwrap(await this.sb.from('groups').select('organization_id').eq('id', groupId).single()) as {
      organization_id: string;
    };
    // [AANNAME] replace-all; an RPC `app.replace_access_rules` would make this atomic.
    unwrap(await this.sb.from('access_rules').delete().eq('group_id', groupId));
    const rows = rules.filter((r) => r.weekdays > 0).map((r) => ({ ...r, group_id: groupId, organization_id: group.organization_id }));
    if (!rows.length) return [];
    return unwrap(await this.sb.from('access_rules').insert(rows).select()) as AccessRule[];
  }

  async listCredentials(orgId: string) {
    return unwrap(await this.sb.from('credentials').select('*').eq('organization_id', orgId)) as Credential[];
  }
  async revokeCredential(credentialId: string) {
    return unwrap(
      await this.sb.from('credentials').update({ status: 'revoke_pending' }).eq('id', credentialId).select().single(),
    ) as Credential;
  }
  async listLockGrants(orgId: string) {
    return unwrap(await this.sb.from('lock_grants').select('*').eq('organization_id', orgId)) as LockGrant[];
  }
  async listLockHardening(orgId: string) {
    return unwrap(
      await this.sb
        .from('lock_hardening')
        .select('lock_id, organization_id, door_id, items, completed, completed_at, recorded_by, updated_at')
        .eq('organization_id', orgId),
    ) as LockHardening[];
  }
  async listSyncJobs(orgId: string | null) {
    let q = this.sb.from('sync_jobs').select('*').order('created_at', { ascending: true }).limit(2000);
    if (orgId) q = q.eq('organization_id', orgId);
    return unwrap(await q) as SyncJob[];
  }

  async listAccessCodes(orgId: string) {
    return unwrap(
      await this.sb
        .from('access_codes')
        .select('id, organization_id, lock_id, kind, label, recipient_name, recipient_contact, valid_from, valid_until, revoked_at, created_by, created_at')
        .eq('organization_id', orgId)
        .order('valid_from', { ascending: false }),
    ) as AccessCode[];
  }
  async createAccessCode(orgId: string, input: AccessCodeInput): Promise<CreatedCode> {
    // Code digits are generated by the provider server-side and stored encrypted.
    const { data, error } = await this.sb.functions.invoke('create-access-code', {
      body: { organization_id: orgId, ...input },
    });
    if (error) throw await toAppError(error, 'create-access-code');
    return data as CreatedCode;
  }
  async revokeAccessCode(codeId: string) {
    unwrap(await this.sb.from('access_codes').update({ revoked_at: new Date().toISOString() }).eq('id', codeId));
  }

  async listAccessEvents(orgId: string, filter: EventFilter = {}) {
    let q = this.sb.from('access_events').select('*').eq('organization_id', orgId).order('occurred_at', { ascending: false }).limit(1000);
    if (filter.door_id) q = q.eq('door_id', filter.door_id);
    if (filter.person_id) q = q.eq('person_id', filter.person_id);
    if (filter.result) q = q.eq('result', filter.result);
    if (filter.since) q = q.gte('occurred_at', filter.since);
    return unwrap(await q) as AccessEvent[];
  }
  async listAuditLog(orgId: string | null) {
    let q = this.sb.from('audit_log').select('id, organization_id, actor_profile_id, actor_role, action, entity, entity_id, created_at').order('created_at', { ascending: false }).limit(500);
    if (orgId) q = q.eq('organization_id', orgId);
    return unwrap(await q) as AuditEntry[];
  }
  async listClubAudit(orgId: string, filter: ClubAuditFilter): Promise<ClubAuditPage> {
    const data = (unwrap(
      await this.sb.rpc('club_audit_log', {
        p_org: orgId,
        p_from: filter.from,
        p_to: filter.to,
        p_category: filter.category,
        p_search: filter.search,
        p_limit: filter.limit,
        p_offset: filter.offset,
      }),
    ) ?? []) as (ClubAuditRow & { total_count: number })[];
    return {
      rows: data.map(({ total_count: _t, ...r }) => ({ ...r, context: r.context ?? {} })),
      total: data[0]?.total_count ?? 0,
    };
  }

  async importPersons(orgId: string, input: ImportInput): Promise<ImportResult> {
    const refs = input.rows.map((r) => r.external_ref).filter(Boolean) as string[];
    const existing = unwrap(
      await this.sb.from('persons').select('id, external_ref').eq('organization_id', orgId).in('external_ref', refs),
    ) as { id: string; external_ref: string }[];
    const existingRefs = new Set(existing.map((e) => e.external_ref));
    const upserted = unwrap(
      await this.sb
        .from('persons')
        .upsert(
          input.rows.map((r) => ({
            organization_id: orgId,
            external_ref: r.external_ref,
            full_name: r.full_name,
            email: r.email,
            phone: r.phone,
            is_minor: r.is_minor,
            archived_at: null,
          })),
          { onConflict: 'organization_id,external_ref' },
        )
        .select('id'),
    ) as { id: string }[];
    let memberships = 0;
    if (input.group_ids.length) {
      const rows = input.group_ids.flatMap((group_id) =>
        upserted.map((p) => ({ group_id, person_id: p.id, valid_until: input.valid_until })),
      );
      const res = unwrap(
        await this.sb.from('group_members').upsert(rows, { onConflict: 'group_id,person_id', ignoreDuplicates: true }).select('person_id'),
      ) as unknown[];
      memberships = res.length;
    }
    const updated = input.rows.filter((r) => r.external_ref && existingRefs.has(r.external_ref)).length;
    return { created: input.rows.length - updated, updated, memberships };
  }

  async listPlatformStaff() {
    return (unwrap(await this.sb.rpc('list_platform_staff')) ?? []) as StaffRow[];
  }
  async inviteStaff(input: StaffInviteInput): Promise<StaffInviteResult> {
    const { data, error } = await this.sb.functions.invoke('invite-staff', { body: input });
    if (error) throw await toAppError(error, 'invite-staff');
    return data as StaffInviteResult;
  }
  async setStaffRole(profileId: string, role: PlatformRole) {
    unwrap(await this.sb.rpc('set_platform_staff_role', { p_profile_id: profileId, p_role: role }));
  }
  async removeStaff(profileId: string) {
    unwrap(await this.sb.rpc('remove_platform_staff', { p_profile_id: profileId }));
  }

  async listInstallers(): Promise<InstallerRow[]> {
    const staff = unwrap(
      await this.sb.from('platform_staff').select('profile_id, profile:profiles(id, full_name, phone, locale)').in('role', ['installer', 'platform_admin']),
    ) as unknown as { profile_id: string; profile: Profile }[];
    const assignments = unwrap(await this.sb.from('installer_assignments').select('*')) as InstallerAssignment[];
    return staff.map((s) => ({ profile: s.profile, assignments: assignments.filter((a) => a.profile_id === s.profile_id) }));
  }
  async enableImpersonationWrite(orgId: string, reason: string) {
    unwrap(await this.sb.rpc('enable_impersonation_write', { p_organization_id: orgId, p_reason: reason }));
  }
  async startImpersonation(orgId: string, reason: string) {
    unwrap(await this.sb.rpc('start_impersonation', { p_organization_id: orgId, p_reason: reason }));
  }
  async assignInstaller(profileId: string, orgId: string, validUntil: string) {
    unwrap(
      await this.sb
        .from('installer_assignments')
        .upsert({ profile_id: profileId, organization_id: orgId, valid_until: validUntil }),
    );
  }
}
